import { client } from './backend/client'
import { FieldErrors, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, fieldErrorsFrom, passwordFieldErrors } from './profile'

// An account as the admin routes render it, in the names this codebase spells
// them with.
//
// The same eight fields `profile.ts` holds for the caller's own record, and
// spelled out again here rather than imported. That is not a second source of
// truth about the *server* -- there is one account shape in this application,
// `Metalist.HTTP.View.user/1`, and it is an allow list of eight fields -- but it
// is a second statement about what *this screen* may render. Each module naming
// the fields it is entitled to show is what makes a column added to `users` later
// invisible to both screens until somebody decides otherwise, rather than arriving
// in one of them because the other had already mentioned it.
export interface ManagedAccount {
  id: string
  username: string
  name: string
  email: string
  active: boolean
  isAdmin: boolean
  insertedAt: string
  updatedAt: string
}

// The account as it arrives: `View.user/1` in the server's own field names.
// `is_admin` is renamed on the way in, which is what `SessionUser` does with it
// too and why the field is spelled this way here.
interface AccountResponse {
  id: string
  username: string
  name: string
  email: string
  active: boolean
  // eslint-disable-next-line camelcase
  is_admin: boolean
  // eslint-disable-next-line camelcase
  inserted_at: string
  // eslint-disable-next-line camelcase
  updated_at: string
}

function accountFrom (response: AccountResponse): ManagedAccount {
  return {
    id: response.id,
    username: response.username,
    name: response.name,
    email: response.email,
    active: response.active,
    isAdmin: response.is_admin,
    insertedAt: response.inserted_at,
    updatedAt: response.updated_at
  }
}

// Every account on file, disabled ones included.
//
// `Accounts.all_users/0` is `Repo.all/1` with no filter, so an account that was
// disabled is on this list and stays on it. That is what the screen needs: an
// admin who disables somebody has to be able to find them again to re-enable
// them, and a list that hid disabled accounts would make both of this ticket's
// honest labels -- "disabling ends the sessions already open" and "re-enabling
// does not bring them back" -- descriptions of something that had disappeared.
//
// Read through the authenticated client, not the public one: this namespace is
// behind `Metalist.AuthPlug` and `admin_only/2`, and the public client sends no
// token at all -- see `backend/client.ts` for why there are two.
export async function loadAccounts (): Promise<ManagedAccount[]> {
  const response = await client.get('/users')

  return response.data.map((account: AccountResponse) => accountFrom(account))
}

// One account, by its identifier. The read the list's rows link to, and the one
// that decides whether a change starts from what the server currently holds
// rather than from what this screen last saw.
//
// `404` is thrown rather than resolved as an absent account, for the same reason
// a `403` on the list above is: the answer carries no fields, so a client reading
// "nothing in the body" as "nothing to show" would render a form of blanks
// instead of saying the account is not there. The route answers `404` with an
// empty body, which is exactly the case that cannot be read as data.
export async function loadAccount (userId: string): Promise<ManagedAccount> {
  const response = await client.get(`/users/${userId}`)

  return accountFrom(response.data)
}

// A refusal from any of the admin routes.
//
// The same two-level shape `ProfileRefused` has, and for the same reason: `fields`
// is what to show against an input and `message` is the one thing to say when the
// server named no field at all. Both on one error rather than two ways out, because
// a caller that has to ask which it caught can get it wrong in the direction that
// matters here -- treating a refusal as a success is what puts an account on a list
// that is still disabled.
//
// Declared beside `ProfileRefused` rather than imported from `../profile`, because
// a class called `ProfileRefused` thrown by the *admin* screen would tell a reader
// of a stack trace something untrue. What is shared is the reading of a body, and
// that is shared by import below.
export class AdminRefused extends Error {
  readonly fields: FieldErrors

  constructor (fields: FieldErrors, message: string) {
    super(message)
    this.name = 'AdminRefused'
    this.fields = fields
  }
}

// The whole of what the create form holds. Every field is one the form has, so a
// caller cannot describe a create without describing all of it -- `admin` included,
// as a boolean that is always sent rather than an optional that is sometimes left
// out. The alternative is an optional flag whose absence the server reads as the
// stored default, which is a decision made here by not saying anything.
export interface NewAccountFields {
  name: string
  username: string
  email: string
  password: string
  admin: boolean
}

// Said on the create form, before anything is submitted, and said again once the
// account exists (`createdNotice/1`).
//
// The claim is the server's: `admin_registration_changeset/2` does not cast
// `:active` at all and puts the account active unconditionally, and the backend's
// own test proves the created account signs in without an intervening step. There
// is no sign-up and no activation route, so this is the only way an account comes
// into being -- which makes "it works now" the thing an admin has to be told, and
// "there is nothing more to do" the thing they must not have to guess at.
export const ACCOUNT_IS_READY_ON_CREATION =
  'The account is created active and can sign in straight away: there is no ' +
  'activation step, so the password you set here is the one to hand over.'

// What to say once the account exists, naming it.
//
// Both halves on purpose. That it works now, and that the credential is the one
// just set -- because the admin's other question, on the same screen at the same
// moment, is what they write down and hand over. "Created" alone answers neither.
export function createdNotice (account: ManagedAccount): string {
  return `${account.username} is active and ready to sign in with the password you ` +
    `just set. Hand that over as it is; there is no activation step for them to wait for.`
}

// What the create form should hold once an attempt has finished.
//
// Dropped on a created account, kept on a refusal -- the same trade-off
// `profile.ts` makes for the password form, and for the same reasons. After a
// success the password is the account's credential and the rest is a draft of an
// account that now exists somewhere else, so none of it belongs in a form the
// admin is walking away from. After a refusal nothing was created, every value is
// still the attempt, and the commonest failure is an address or a username somebody
// else already holds -- asking for those again is the form defending itself against
// a mistake that costs the admin nothing.
//
// `created` rather than a refusal or an error, because there are two outcomes to
// hand over and a parameter naming them would put the difference between "refused"
// and "could not be reached" into a signature for a decision that does not care.
export function newAccountFormAfter (attempted: NewAccountFields, created: boolean): NewAccountFields {
  if (created) {
    return { name: '', username: '', email: '', password: '', admin: false }
  }

  return attempted
}

// Hands out an account, with a password the admin can give to whoever will use it.
//
// `201` and the account, rendered through the same `View.user/1` as every other
// account route, so this response is not a place a plaintext password could arrive
// from -- `register_user/1` answers with a struct whose virtual `:password` still
// holds the real one, and the view's allow list is what keeps it out.
//
// Throws rather than resolving on a refusal, for the reason above: the outcome that
// has to be told apart is the account that exists from the one that does not, and a
// create that resolved with something to render would let the screen announce an
// account the server never made.
export async function createAccount (fields: NewAccountFields): Promise<ManagedAccount> {
  const tooShort = passwordFieldErrors(fields.password)

  // Refused here rather than sent, so the answer is a field error against the
  // password field and no request is made. The bounds are the server's, and the
  // client keeps one copy of them -- `profile.ts`, which is where `8..128` is
  // asserted as literals -- because a second copy is a second thing to be wrong. It
  // is a saved round trip and not the control: the server applies the same bound to
  // the same field and would refuse a value this let through.
  if (Object.keys(tooShort).length > 0) {
    throw new AdminRefused(tooShort, 'Creating the account did not work. Please try again.')
  }

  let response

  try {
    response = await client.post('/users', {
      username: fields.username,
      name: fields.name,
      email: fields.email,
      password: fields.password,
      // Sent whether it is `true` or `false`. `admin_registration_changeset/2`
      // casts it, so `false` is a real answer -- the stored default -- rather than
      // an absence the server has to fill in. The alternative, omitting it when
      // false, makes the body mean two different things depending on a checkbox.
      is_admin: fields.admin
    })
  } catch (error) {
    throw refusalFrom(error, 'Creating the account did not work. Please try again.')
  }

  return accountFrom(response.data)
}

// The bounds on the password an admin sets, re-exported rather than repeated, for
// the create form's hint. `profile.ts` is the one place the client states them.
export const NEW_PASSWORD_MIN_LENGTH = PASSWORD_MIN_LENGTH
export const NEW_PASSWORD_MAX_LENGTH = PASSWORD_MAX_LENGTH

// What the update form holds. One field per privilege the admin may exercise over
// somebody else's account, and `name` and `email` as the corrections #08 mentions.
//
// `username` is not among them and cannot be: it is fixed at creation for an admin
// exactly as for anybody else, and `admin_update_changeset/3` refuses a submitted one
// that differs rather than casting it. The rule and its reasoning live on the backend
// in `user.ex`; this shape is the client of that rule, not a second decision. Asking
// for a field the server will not write would be a form that reports a refusal naming
// something the person never got to type.
//
// `password` is not among them either, despite the server casting one: resetting a
// credential is its own function below (`resetPassword/2`), because it is the one
// update that also revokes every session the account holds, and the interface has to
// say so.
export interface UpdateFields {
  name: string
  email: string
  active: boolean
  admin: boolean
}

// What to say about disabling, and about re-enabling.
//
// The ticket asks for both to be stated honestly, and the honesty is specific rather
// than general: disabling removes access from sessions already open, and re-enabling
// does not give those sessions back. The server enforces both by moving the account's
// token version, and the tests for that assert on a token that stops working rather
// than on the version itself -- so the interface must not describe the mechanism
// either. What a person needs to know is what happens to them, in the order it
// happens.
//
// The third claim is the one most easily got wrong: re-enabling does not simply undo
// the disable. `#05` increments the version on the disable, so the tokens issued
// before it stay dead afterwards. A control labelled "deactivate" that quietly did
// something else would be worse than no control -- it invites an admin to re-enable a
// person they disabled for a reason and believe the account has walked back in.
export const DISABLING_REMOVES_OPEN_SESSIONS =
  'They are signed out where they are right now: any session they already have open stops working.'

export const RE_ENABLING_DOES_NOT_RESTORE_SESSIONS =
  'That person can sign in again, but any session they had open before the disable stays dead.'

// The refusal that is about *self* rather than about admins generally.
//
// `admin_update_changeset/3` refuses a request from the acting admin that would change
// that admin's own active state or admin rights, and the backend proves the refusal is
// about self by asserting the identical request against a **second** admin succeeds.
// Without that, every screen that hides these controls paints a picture the server
// does not hold: it looks as if no admin may disable any admin, and it is silent about
// the one case that is true.
//
// So this is not a client-side rule and not a courtesy. The screen disables the
// controls and says why; the server refuses regardless. A client that only hid the
// controls would give an admin a button they could still press with a planted request,
// and one that only relied on the server would show them a control that always fails.
export const SELF_REFUSAL =
  'You cannot change your own active state or your own admin rights: with one admin, doing so would lock everybody out of user management. Ask another admin to do it for you.'

// The update form's starting values, read out of the account rather than out of
// defaults.
//
// A `true`/`false` default for a peer account would be a guess about a person this
// screen has supposedly read, and it would be the guess that disables an account
// nobody asked to disable. The account is the only source.
export function updateFieldsFor (account: ManagedAccount): UpdateFields {
  return {
    name: account.name,
    email: account.email,
    active: account.active,
    admin: account.admin
  }
}

// Whether the account in front of the screen is the acting admin's own. The
// decision of every self-protection on this screen, in one place, so the view does
// not inline an id comparison that nothing tests.
//
// Two ids, compared raw: ids are opaque strings and equal ids are the only way to
// be the same account. A normalised comparison would be a rule invented here, and a
// flag passed in from the caller would be the same decision moved somewhere nothing
// verifies.
//
// An absent session is not the acting admin's own anything, and says so by
// answering `false`: the screen that is not signed in is on its way out, and the
// response interceptor has already dropped the session and sent the visitor to the
// sign-in form. A `true` here would protect the fields on a screen nobody sees.
export function isOwnAccount (actingAdminId: string | null | undefined, accountId: string): boolean {
  return actingAdminId != null && actingAdminId === accountId
}

// Of the four fields the server casts, the two this form may not change when the
// account is the acting admin's own. Both are about *self*, and the refusal that
// produces this is the server's (`admin_update_changeset/3`) -- this is the client
// half of the same rule, so the two agree rather than one overriding the other.
//
// The other two are always changeable, including on the acting admin's own account:
// an admin correcting their own name and email is ordinary and allowed, and the
// backend proves it. Hiding the whole form on self would take an ordinary
// correction away.
export function selfProtectedFields (isOwn: boolean): { active: boolean; admin: boolean } {
  return isOwn ? { active: true, admin: true } : { active: false, admin: false }
}

// Applies a change to an account.
//
// `is_admin` is sent whether it is `true` or `false`, for the reason `createAccount`
// sends it: a boolean that is always present is one answer, and an omission the server
// reads as its default is a second thing for the body to mean.
export async function updateAccount (
  userId: string,
  fields: UpdateFields
): Promise<ManagedAccount> {
  let response

  try {
    response = await client.put(`/users/${userId}`, {
      name: fields.name,
      email: fields.email,
      active: fields.active,
      is_admin: fields.admin
    })
  } catch (error) {
    throw refusalFrom(error, 'Saving the account did not work. Please try again.')
  }

  return accountFrom(response.data)
}

// Resets a lost credential.
//
// This is the one update that takes a new password rather than a correction, and the
// one that ends every session the account holds -- including the one the request was
// made with, if the account being reset is the acting admin's own. The client cannot
// tell from here whether that happened, and does not need to: the next request carries
// a dead token, and the response interceptor has already dropped the session and sent
// the visitor to sign in by the time anything above it sees the failure.
//
// Which is why the password is dropped from the form on success, for the reason
// `profile.ts` gives: after a success the credential belongs to the account and not to
// a form the admin is walking away from.
export async function resetPassword (
  userId: string,
  password: string
): Promise<ManagedAccount> {
  const tooShort = passwordFieldErrors(password)

  if (Object.keys(tooShort).length > 0) {
    throw new AdminRefused(tooShort, 'Resetting the password did not work. Please try again.')
  }

  let response

  try {
    response = await client.put(`/users/${userId}`, { password })
  } catch (error) {
    throw refusalFrom(error, 'Resetting the password did not work. Please try again.')
  }

  return accountFrom(response.data)
}

// Turns a thrown error into the refusal a screen renders, in one place, because
// four routes must not disagree about it.
//
// A `401` is not handled specially here and does not need to be: the response
// interceptor has already dropped the session and sent the visitor to the sign-in
// form by the time this sees it, and the screen is on its way out. Reading one as a
// field-level refusal would put a second, contradicting message on a page nobody is
// on. The fields are still read, because `fieldErrorsFrom/1` finds nothing in a
// `401` body and the whole-form message below is what a caller falls back to --
// which is the honest answer for it, since this screen is being left.
//
// Anything that never reached the server is re-thrown as itself. Reporting it as a
// refusal would be a claim that the server turned the request down, which nobody
// knows.
function refusalFrom (error: unknown, message: string): AdminRefused {
  if ((error as any).response === undefined) {
    throw error
  }

  return new AdminRefused(fieldErrorsFrom((error as any).response.data), message)
}
